import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { getUser, removeToken, isAuthenticated, isTeacher, isAdmin } from '@/utils/auth';
import { GraduationCap, LogOut, LayoutDashboard, Shield, Menu, X, Info, HelpCircle, ShoppingBag } from 'lucide-react';
import { AboutDialog } from './AboutDialog';
import ContactSupportDialog from './ContactSupportDialog';

// Set to true to show Get Started inside the mobile hamburger menu again
const SHOW_MOBILE_GET_STARTED = false;
// Insight Warehouse menu item appears once this many approved products are listed (same as landing showcase)
const MIN_WAREHOUSE_PRODUCTS = 6;
import { AuthModal } from './AuthModal';
import { RoleSelectionModal } from './RoleSelectionModal';
import { CurrencySelector } from './CurrencySelector';

export const Navbar = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [authDefaultRole, setAuthDefaultRole] = useState('student');
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showWarehouse, setShowWarehouse] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get('/marketplace/products', { params: { limit: 1 } })
      .then((res) => setShowWarehouse((res.data.total || 0) >= MIN_WAREHOUSE_PRODUCTS))
      .catch(() => setShowWarehouse(false));
  }, []);

  useEffect(() => {
    // Initial check
    if (isAuthenticated()) {
      setUser(getUser());
    }
    
    // Listen for storage changes (login/logout from other tabs or same tab)
    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === 'user' || e.key === null) {
        if (isAuthenticated()) {
          setUser(getUser());
        } else {
          setUser(null);
        }
      }
    };
    
    // Custom event for same-tab auth changes
    const handleAuthChange = () => {
      if (isAuthenticated()) {
        setUser(getUser());
      } else {
        setUser(null);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authChange', handleAuthChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChange', handleAuthChange);
    };
  }, []);

  const handleLogout = () => {
    removeToken();
    setUser(null);
    navigate('/');
  };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setShowAdminAuth(false);
    if (userData.role === 'admin') {
      navigate('/admin/dashboard');
    } else if (userData.role === 'teacher') {
      navigate('/teacher/dashboard');
    } else if (userData.role === 'seller') {
      navigate('/seller/dashboard');
    } else {
      navigate('/student/dashboard');
    }
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    if (user.role === 'admin') return '/admin/dashboard';
    if (user.role === 'teacher') return '/teacher/dashboard';
    if (user.role === 'seller') return '/seller/dashboard';
    return '/student/dashboard';
  };

  // Handle role selection from the modal
  const handleRoleSelected = (selectedRole) => {
    setShowRoleSelection(false);
    setAuthDefaultRole(selectedRole);
    setShowAuth(true);
  };

  // Close mobile menu when navigating
  const handleNavigation = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <>
      <nav className="glassmorphism-header sticky top-0 z-50" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Left side - Admin button + Logo */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Admin access - only visible to logged-in admins */}
              {user && user.role === 'admin' && (
                <Link
                  to="/admin/dashboard"
                  className="flex items-center justify-center w-8 h-8 rounded-full transition-all hover:bg-blue-100"
                  style={{ color: '#2563EB' }}
                  title="Admin Dashboard"
                  data-testid="admin-access-button"
                >
                  <Shield className="h-5 w-5" />
                </Link>
              )}
              
              <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
                <GraduationCap className="h-7 w-7 sm:h-8 sm:w-8" style={{ color: '#163329' }} />
                <span className="text-lg sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                  Insight Evolution
                </span>
              </Link>
            </div>

            <div className="flex items-center gap-3">
            {/* Desktop Navigation - hidden on mobile */}
            <div className="hidden md:flex flex-col items-end gap-2">
              {/* Currency Selector - on top */}
              <CurrencySelector compact={true} />
              
              {/* Get Started button - below USD */}
              {user ? (
                <div className="flex items-center gap-2">
                  <Link to={getDashboardLink()}>
                    <Button
                      variant="default"
                      className="rounded-full"
                      data-testid="dashboard-button"
                    >
                      {user.role === 'admin' ? (
                        <Shield className="h-4 w-4 mr-2" />
                      ) : (
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                      )}
                      {user.role === 'admin' ? 'Admin' : 'Dashboard'}
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    onClick={handleLogout}
                    data-testid="logout-button"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowRoleSelection(true)}
                  className="rounded-full"
                  data-testid="get-started-button"
                >
                  Get Started
                </Button>
              )}
            </div>

            {/* Hamburger Menu Button - all screen sizes */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex items-center justify-center w-10 h-10 rounded-lg transition-colors"
              style={mobileMenuOpen ? { color: '#FFFFFF', backgroundColor: '#163329' } : { color: '#163329' }}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
            </div>
          </div>

          {/* Menu Dropdown */}
          {mobileMenuOpen && (
            <div 
              className="mt-4 pb-4 border-t border-gray-200 pt-4 animate-in slide-in-from-top-2 duration-200"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col gap-3 md:max-w-xs md:ml-auto">
                {/* Currency Selector - desktop already shows it in the header */}
                <div className="flex justify-center pb-3 border-b border-gray-100 md:hidden">
                  <CurrencySelector compact={true} />
                </div>
                
                {showWarehouse && (
                <Button
                  variant="outline"
                  className="w-full rounded-full justify-center"
                  style={location.pathname.startsWith('/marketplace') ? { backgroundColor: '#163329', color: '#FFFFFF', borderColor: '#163329' } : undefined}
                  aria-current={location.pathname.startsWith('/marketplace') ? 'page' : undefined}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/marketplace');
                  }}
                  data-testid="marketplace-button"
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Insight Warehouse
                </Button>
                )}
                <AboutDialog
                  trigger={
                    <Button variant="outline" className="w-full rounded-full justify-center" data-testid="mobile-about-button">
                      <Info className="h-4 w-4 mr-2" />
                      About
                    </Button>
                  }
                />
                <ContactSupportDialog
                  trigger={
                    <Button variant="outline" className="w-full rounded-full justify-center" data-testid="mobile-contact-support-button">
                      <HelpCircle className="h-4 w-4 mr-2" />
                      Support
                    </Button>
                  }
                />

                {user ? (
                  <div className="flex flex-col gap-3 md:hidden">
                    {/* Dashboard Button */}
                    <Button
                      variant="default"
                      className="w-full rounded-full justify-center"
                      onClick={() => handleNavigation(getDashboardLink())}
                      data-testid="mobile-dashboard-button"
                    >
                      {user.role === 'admin' ? (
                        <Shield className="h-4 w-4 mr-2" />
                      ) : (
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                      )}
                      {user.role === 'admin' ? 'Admin Dashboard' : 'Dashboard'}
                    </Button>
                    
                    {/* Logout Button */}
                    <Button
                      variant="outline"
                      className="w-full rounded-full justify-center"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleLogout();
                      }}
                      data-testid="mobile-logout-button"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </Button>
                  </div>
                ) : SHOW_MOBILE_GET_STARTED && (
                  <Button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setShowRoleSelection(true);
                    }}
                    className="w-full rounded-full justify-center"
                    data-testid="mobile-get-started-button"
                  >
                    Get Started
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Role Selection Modal */}
      <RoleSelectionModal
        open={showRoleSelection}
        onClose={() => setShowRoleSelection(false)}
        onSelectRole={handleRoleSelected}
      />

      {/* Regular auth modal for teachers/students */}
      <AuthModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={handleAuthSuccess}
        defaultRole={authDefaultRole}
        lockRole={true}
      />
      
      {/* Admin auth modal - login only */}
      <AuthModal
        open={showAdminAuth}
        onClose={() => setShowAdminAuth(false)}
        onSuccess={handleAuthSuccess}
        adminOnly={true}
      />
    </>
  );
};